// Shared wiring for the v2 deal/contact activity-timeline
// search (CON-146). The search field lives in the shared
// `ActivityFeed`; each surface owns the debounced partial
// reload that re-fetches its activity props with (or without)
// the `activity_search` param. Centralized here so the param
// name and the deal-show `only:` manifest live in one place —
// a rename fails loudly in the surface specs that assert
// these arrays rather than silently shipping stale rows
// (CLAUDE.md only:/reset: rule).

import { router } from "@inertiajs/react"
import type { ReloadOptions } from "@inertiajs/core"

// URL/param name the backend reads (`params[:activity_search]`).
// snake_case because Inertia `data` / `only:` keys are matched
// against the server-side names.
export const ACTIVITY_SEARCH_PARAM = "activity_search"

// Read the current `activity_search` value off the URL so a
// surface can seed the search field on first paint (landing on
// a `?activity_search=…` link survives a refresh). Empty string
// when absent or outside the browser.
export function currentActivitySearch(): string {
  if (typeof window === "undefined") return ""
  return (
    new URLSearchParams(window.location.search).get(
      ACTIVITY_SEARCH_PARAM,
    ) ?? ""
  )
}

// The shared debounced-search reload every activity surface
// uses. Takes the surface's path + the snake_case `only:`
// manifest for its activity props, and the new search value.
// It rewrites ONLY the `activity_search` param on the current
// URL — setting it when non-blank, dropping it when blank
// (which restores the default merge-on-read feed) — and leaves
// every other param (tab, scope, drawer trail, return_to)
// intact. `preserveState` + `preserveScroll` keep the page
// (and the search input's focus + value, which the
// `ActivityFeed` owns) across the partial reload.
export function runActivitySearch(
  path: string,
  onlyProps: readonly string[],
  value: string,
): void {
  if (typeof window === "undefined") return

  const search = new URLSearchParams(window.location.search)
  const trimmed = value.trim()
  if (trimmed === "") {
    search.delete(ACTIVITY_SEARCH_PARAM)
  } else {
    search.set(ACTIVITY_SEARCH_PARAM, trimmed)
  }

  const qs = search.toString()
  const url = qs ? `${path}?${qs}` : path

  router.get(
    url,
    {},
    {
      only: [...onlyProps],
      preserveState: true,
      preserveScroll: true,
    },
  )
}

// Widened partial-reload of an activity feed's props after a
// row mutation (e.g. deleting a note). `router.reload`'s
// `ReloadOptions` type omits `preserveScroll`/`preserveState`,
// but the underlying visit forwards them at runtime — so we
// widen the options object here once instead of casting at
// every call site. Keeps the feed (and its scroll/state) in
// place while the server re-serializes the changed prop.
export function reloadActivityOnly(onlyProps: readonly string[]): void {
  // `router.reload` preserves scroll + state by default but
  // `ReloadOptions` omits those keys from its type; widen with
  // them explicitly (reload forwards the extras to the
  // underlying visit at runtime), matching the deal-show stream.
  const options: ReloadOptions & {
    preserveScroll?: boolean
    preserveState?: boolean
  } = {
    only: [...onlyProps],
    preserveScroll: true,
    preserveState: true,
  }
  router.reload(options)
}

// `only:` manifest for the DEAL SHOW page's search reload. The
// deal show ships its activity as a single eager `activity`
// prop, so a search reload need only re-fetch that. (The
// contact surfaces reuse `CONTACT_TAB_PROPS.activity` from
// `@/lib/contact-tab-props`; the drawers reuse
// `BASE_RELOAD_PROPS` from `@/lib/drawer-trail`.)
export const DEAL_SHOW_ACTIVITY_PROPS = ["activity"] as const
